import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { PaginatedHeaderCell, RowData, sortDirection } from "../../Types";
import RowSkeleton from "../Table/elements/RowSkeleton";
import "./PaginatedTable.css";

export default class Table extends React.Component<
  {
    header: Array<PaginatedHeaderCell>;
    data: Array<RowData>;
    loaded: boolean;
    skeletonRows: number;
    onClick: any;
    defaultSortKey?: string;
    defaultSortDirection?: sortDirection;
    counter?: boolean;
    useLoader?: boolean;
    updateLoading?: boolean;
    onColumnClick?: any;
    className?: string;
    page: number;
    pageSize: number;
    entryCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onSortChange?: (sortKey: string) => void;
  },
  {
    sortKey?: string;
    sortDirection?: sortDirection | undefined;
  }
> {
  constructor(props: any) {
    super(props);
    this.state = {
      sortKey: this.props.defaultSortKey,
      sortDirection: this.props.defaultSortDirection,
    };
  }

  sortTable(key: string, direction: "asc" | "desc") {
    this.setState({ sortKey: key, sortDirection: direction });
  }

  render() {
    return (
      <table className={`table ${this.props.className || ""}`}>
        <thead>
          <tr>
            {this.props.counter && <th>#</th>}
            {this.props.header.map(
              (currHeader: PaginatedHeaderCell, index: number) => (
                <th
                  key={index}
                  onClick={() => {
                    if (currHeader.onClick) {
                      currHeader.onClick();
                    }
                    if (currHeader.sort) {
                      if (this.props.onColumnClick) {
                        this.props.onColumnClick(index);
                      }

                      // Use external sort handler if provided, otherwise use internal sorting
                      if (this.props.onSortChange) {
                        this.props.onSortChange(currHeader.sort);
                      } else {
                        this.sortTable(
                          currHeader.sort,
                          this.state.sortKey === currHeader.sort
                            ? this.state.sortDirection === "asc"
                              ? "desc"
                              : "asc"
                            : "asc"
                        );
                      }
                    }
                  }}
                  className={`${
                    currHeader.sort || currHeader.onClick ? "pointer" : ""
                  } ${currHeader.hide ? "display-none" : ""} ${
                    this.props.header[index].displayWidth
                      ? `max-width-${this.props.header[index].displayWidth}`
                      : ""
                  }`}
                >
                  {currHeader.title}{" "}
                  {((this.props.onSortChange &&
                    this.props.defaultSortKey === currHeader.sort) ||
                    (!this.props.onSortChange &&
                      this.state.sortKey === currHeader.sort)) &&
                    ((this.props.onSortChange &&
                      this.props.defaultSortDirection === "asc") ||
                    (!this.props.onSortChange &&
                      this.state.sortDirection === "asc") ? (
                      <FontAwesomeIcon icon={faChevronDown} size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faChevronUp} size="sm" />
                    ))}
                </th>
              )
            )}
            {this.props.useLoader && (
              <th>
                <div className="table-loading-container">
                  <ClipLoader
                    color={"gray"}
                    size={20}
                    loading={this.props.updateLoading}
                  />
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {!this.props.loaded && (
            <>
              {[...Array(this.props.skeletonRows)].map((x, i) => (
                <RowSkeleton
                  columns={
                    this.props.header.filter((h) => !h.hide).length +
                    (this.props.counter ? 1 : 0)
                  }
                  noise={i}
                  key={i}
                />
              ))}
            </>
          )}
          {this.props.data.map((currRow: RowData, columnIndex: number) => (
            <tr
              key={columnIndex}
              onClick={() => {
                this.props.onClick(currRow.onClick);
              }}
              style={{
                opacity: currRow.opacity !== undefined ? currRow.opacity : 1,
              }}
              className={`${currRow.onClick ? "pointer" : ""} ${
                currRow.highlight ? "highlight" : ""
              }`}
            >
              {this.props.counter && <td>{columnIndex + 1}</td>}
              {currRow.cells.map((currCell: any, rowIndex: number) => (
                <td
                  key={rowIndex}
                  className={`${currRow.onClick ? "pointer" : ""} ${
                    this.props.header[rowIndex].hide ? "display-none" : ""
                  } ${
                    this.props.header[rowIndex].displayWidth
                      ? `max-width-${this.props.header[rowIndex].displayWidth}`
                      : ""
                  }`}
                >
                  {currCell.value}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td
              className="pagination-controls"
              colSpan={
                this.props.header.filter((h) => !h.hide).length +
                (this.props.counter ? 1 : 0) +
                (this.props.useLoader ? 1 : 0)
              }
            >
              <div className="pagination-container">
                <span>Einträge pro Seite:</span>
                <select
                  value={this.props.pageSize}
                  onChange={(e) =>
                    this.props.onPageSizeChange &&
                    this.props.onPageSizeChange(parseInt(e.target.value))
                  }
                  className="page-size-select"
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                </select>
                <span className="entry-info">
                  {(this.props.page - 1) * this.props.pageSize + 1} -{" "}
                  {Math.min(
                    this.props.page * this.props.pageSize,
                    this.props.entryCount
                  )}{" "}
                  von {this.props.entryCount}
                </span>
                <button
                  className="pagination-btn"
                  disabled={this.props.page <= 1}
                  onClick={() =>
                    this.props.onPageChange &&
                    this.props.onPageChange(this.props.page! - 1)
                  }
                >
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    rotation={90}
                    size="sm"
                  />
                </button>
                <button
                  className="pagination-btn"
                  disabled={
                    this.props.page >=
                    Math.ceil(this.props.entryCount / this.props.pageSize)
                  }
                  onClick={() =>
                    this.props.onPageChange &&
                    this.props.onPageChange(this.props.page! + 1)
                  }
                >
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    rotation={270}
                    size="sm"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
}
